import { Button } from '@finn/design-system/atoms/button';
import { useVoucherStore, VoucherClass } from '@finn/ua-vehicle';
import { App, cn, useCurrentApp, useIntl } from '@finn/ui-utils';
import { getLocalstorageValue, StorageKeys } from '@finn/ui-utils/ssr';
import { useCallback } from 'react';

import {
  setForBusinessCookie,
  setMonthlyPaymentOfServiceFeeCookie,
} from '../../../helpers';
import { FilterKey, FilterValuesObject } from '../../core';
import {
  useFilterValues,
  useReplaceFilterValues,
  useResultsCount,
} from '../../filters-management';
import { trackFiltersCleared } from '../../tracking';
import { FilterTagList } from './FilterTagList';
import { SortBy } from './individual-filters/SortBy';

const removeProductGroupFilter = (
  filters: FilterValuesObject
): FilterValuesObject => {
  return Object.fromEntries(
    Object.entries(filters).filter(([key]) => key !== FilterKey.PRODUCT_GROUP)
  );
};

export const CombinedFiltersTags = ({
  totalResults,
}: {
  totalResults?: number;
}) => {
  const i18n = useIntl();
  // TODO: remove for advanced grouping rollout in B2B
  const isB2BApp = useCurrentApp() === App.B2B;
  const resultsCount = useResultsCount() || totalResults || 0;
  const filterValues = useFilterValues();
  const replaceFilterValues = useReplaceFilterValues();
  const voucherData = useVoucherStore((state) => state.referralVoucherData);
  const hasDelayVoucher = voucherData?.class === VoucherClass.delay_management;

  const filteredFilterValues = removeProductGroupFilter(filterValues);
  const appliedFiltersCount = Object.entries(filteredFilterValues).length ?? 0;

  const handleReset = useCallback(() => {
    const delay = getLocalstorageValue(StorageKeys.DELAY_MANAGEMENT);
    const hasDelayDatesInLocalStorage =
      delay?.available_from && delay?.available_to;
    if (hasDelayVoucher && hasDelayDatesInLocalStorage) {
      replaceFilterValues({
        available_from: delay?.available_from,
        available_to: delay?.available_to,
      });
    } else {
      replaceFilterValues({});
    }
    setForBusinessCookie(false);
    setMonthlyPaymentOfServiceFeeCookie(false);
    trackFiltersCleared();
  }, [replaceFilterValues, hasDelayVoucher]);

  let resultsLabel =
    resultsCount > 1
      ? i18n.formatMessage('plp.cars')
      : i18n.formatMessage('plp.car');
  if (!isB2BApp) {
    resultsLabel = resultsCount > 1 ? 'Modelle' : 'Modell';
  }

  const minFilterForReset = hasDelayVoucher ? 2 : 0;

  return (
    <div
      className={cn(
        'z-[1] flex items-center bg-white md:top-0',
        'sticky top-[72px] mb-1',
        'md:min-h-auto h-10 min-h-0 overflow-hidden md:min-h-12 md:overflow-auto'
      )}
    >
      <div className="flex items-center">
        <div className="inline-block p-2.5 pl-0 pr-2 body-12-light">
          {`${resultsCount} ${resultsLabel}`}
        </div>
        {appliedFiltersCount > minFilterForReset && (
          <Button
            variant="action"
            size="sm"
            className="mr-4"
            data-testid="reset-filters"
            onClick={handleReset}
          >
            {i18n.formatMessage('plp.clearFilter')}
          </Button>
        )}
      </div>

      <>
        <div className="relative mr-2 min-h-[29px] grow">
          <div className={cn('absolute inset-0 size-full')}>
            <FilterTagList />
          </div>
        </div>
      </>

      <div className="hidden flex-row flex-nowrap gap-2 md:flex">
        <SortBy isMobile={false} />
      </div>
    </div>
  );
};
